import React, { useState, useEffect, useRef } from "react";
import {
  MessageCircle,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  Send,
  X,
  Minimize2,
  Maximize2,
  Book,
  Loader2,
  Square,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import { callAIFunction, callAIFunctionStream } from "../lib/openai";
import { useSermons } from "../hooks/useSermons";
import { useAuth } from "../hooks/useAuth";
import { supabase } from "../lib/supabase";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
}

interface SpeechRecognitionEvent extends Event {
  readonly results: SpeechRecognitionResultList;
  readonly resultIndex: number;
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult:
    | ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any)
    | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: Event) => any) | null;
}

declare global {
  interface Window {
    SpeechRecognition: {
      prototype: SpeechRecognition;
      new (): SpeechRecognition;
    };
    webkitSpeechRecognition: {
      prototype: SpeechRecognition;
      new (): SpeechRecognition;
    };
  }
}

interface TheologicalAssistantProps {
  mode?: 'float' | 'panel';
  sermonId?: string;
}

export function TheologicalAssistant({ mode = 'float', sermonId }: TheologicalAssistantProps) {
  const { sermons } = useSermons();
  const { user } = useAuth();
  const [ministryProfile, setMinistryProfile] = useState<{
    role?: string;
    denomination?: string;
    congregationSize?: string;
    useCases?: string[];
  }>({});
  const [isOpen, setIsOpen] = useState(false);
  const [focusedSermonId, setFocusedSermonId] = useState<string | null>(sermonId ?? null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const streamingMsgIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Voice states
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [useOpenAIVoice, setUseOpenAIVoice] = useState(false);
  const [openAIVoice, setOpenAIVoice] = useState<
    "alloy" | "echo" | "fable" | "onyx" | "nova" | "shimmer"
  >("alloy");
  const [conversationalMode, setConversationalMode] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const synthesisRef = useRef<SpeechSynthesis | null>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("ministry_role, denomination, congregation_size, primary_use_cases")
      .eq("id", user.id)
      .single()
      .then(({ data }) => {
        if (!data) return;
        setMinistryProfile({
          role: data.ministry_role || undefined,
          denomination: data.denomination || undefined,
          congregationSize: data.congregation_size || undefined,
          useCases: data.primary_use_cases || undefined,
        });
      });
  }, [user]);

  useEffect(() => {
    // Mobile detection
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);

    // Check for speech recognition support
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    const speechSynthesis = window.speechSynthesis;

    if (SpeechRecognition && speechSynthesis) {
      setSpeechSupported(true);
      synthesisRef.current = speechSynthesis;

      // Initialize voices (sometimes they need time to load)
      const initVoices = () => {
        const voices = speechSynthesis.getVoices();
        if (voices.length > 0) {
          console.log(
            "Available voices:",
            voices.map((v) => ({
              name: v.name,
              lang: v.lang,
              quality: v.localService ? "Local" : "Network",
            })),
          );
        }
      };

      // Load voices immediately if available
      initVoices();

      // Also listen for voice loading event
      speechSynthesis.addEventListener("voiceschanged", initVoices);

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      let finalTranscript = '';
      let silenceTimer: NodeJS.Timeout | null = null;
      
      recognition.onresult = (event: SpeechRecognitionEvent) => {
        let interimTranscript = '';
        
        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }
        
        // Show the combined transcript in real-time
        setInputText((finalTranscript + interimTranscript).trim());
        
        // Reset silence timer on any speech
        if (silenceTimer) {
          clearTimeout(silenceTimer);
        }
        
        // Set a timer to stop listening after 3 seconds of silence
        silenceTimer = setTimeout(() => {
          if (recognitionRef.current) {
            recognitionRef.current.stop();
          }
        }, 3000);
      };

      recognition.onend = () => {
        setIsListening(false);
        if (silenceTimer) {
          clearTimeout(silenceTimer);
          silenceTimer = null;
        }
        
        // In conversational mode, auto-send the message if there's content
        if (conversationalMode && finalTranscript.trim()) {
          console.log("Auto-sending message in conversational mode:", finalTranscript.trim());
          // Use the finalTranscript directly since inputText might not be updated yet
          const messageToSend = finalTranscript.trim();
          
          // Clear the input text and transcript immediately
          setInputText("");
          finalTranscript = '';
          
          // Create and send the user message immediately
          const userMessage = {
            id: Date.now().toString(),
            role: "user" as const,
            content: messageToSend,
            timestamp: new Date(),
          };
          
          setMessages((prev) => [...prev, userMessage]);
          setIsLoading(true);
          
          // Process the message
          processMessage(messageToSend);
        } else {
          // Reset the transcript accumulator
          finalTranscript = '';
        }
      };

      recognition.onerror = (event: any) => {
        console.log('Speech recognition error:', event.error);
        setIsListening(false);
        if (silenceTimer) {
          clearTimeout(silenceTimer);
          silenceTimer = null;
        }
        finalTranscript = '';
      };

      recognitionRef.current = recognition;
    }

    // Add welcome message
    if (messages.length === 0) {
      setMessages([
        {
          id: "1",
          role: "assistant",
          content:
            "Hello! I'm your theological research assistant. I'm here to help you explore Scripture, discuss theological concepts, and provide biblical insights for your study and ministry preparation.\n\n*I have access to your sermon library and can provide feedback on your sermons, help you find connections between messages, explore theological themes across your work, and answer questions about specific sermon content.*\n\n*Note: I focus on research assistance rather than creating sermons. I can help you understand passages, explore theological concepts, and provide study resources to inform your own message preparation.*\n\nWhat would you like to explore today?",
          timestamp: new Date(),
        },
      ]);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const startListening = () => {
    if (recognitionRef.current && !isListening) {
      setInputText(''); // Clear any previous text
      setIsListening(true);
      recognitionRef.current.start();
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  };

  const speakText = (text: string) => {
    if (synthesisRef.current && voiceEnabled) {
      // Stop any ongoing speech
      synthesisRef.current.cancel();

      const utterance = new SpeechSynthesisUtterance(text);

      // Get available voices and select the best one
      const voices = synthesisRef.current.getVoices();

      // Prioritize high-quality voices
      const preferredVoices = [
        // Google voices (usually high quality)
        voices.find(
          (voice) =>
            voice.name.includes("Google") && voice.lang.startsWith("en"),
        ),
        // Microsoft voices (also good quality)
        voices.find(
          (voice) =>
            voice.name.includes("Microsoft") && voice.lang.startsWith("en"),
        ),
        // Apple voices (on Safari/Mac)
        voices.find(
          (voice) =>
            voice.name.includes("Alex") || voice.name.includes("Samantha"),
        ),
        // Enhanced or Premium voices
        voices.find(
          (voice) =>
            (voice.name.includes("Enhanced") ||
              voice.name.includes("Premium")) &&
            voice.lang.startsWith("en"),
        ),
        // Natural sounding voices
        voices.find(
          (voice) =>
            voice.name.includes("Natural") && voice.lang.startsWith("en"),
        ),
        // Any English voice
        voices.find((voice) => voice.lang.startsWith("en-US")),
        voices.find((voice) => voice.lang.startsWith("en")),
      ];

      // Use the first available preferred voice
      const selectedVoice = preferredVoices.find((voice) => voice);
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      // Optimize speech parameters for natural sound
      utterance.rate = 0.85; // Slightly slower for clarity
      utterance.pitch = 0.95; // Slightly lower pitch sounds more natural
      utterance.volume = 0.8;

      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => {
        setIsSpeaking(false);
        
        // Auto-reengage microphone in conversational mode
        if (conversationalMode && voiceEnabled && speechSupported && !isListening) {
          console.log("Re-engaging microphone after browser TTS");
          setTimeout(() => {
            startListening();
          }, 750); // Small delay for natural flow
        }
      };
      utterance.onerror = () => setIsSpeaking(false);

      synthesisRef.current.speak(utterance);
    }
  };

  const playSecondChunk = async (audioData: string) => {
    try {
      // Extract base64 data from data URL
      let base64Data = audioData;
      if (audioData.includes(',')) {
        base64Data = audioData.split(',')[1];
      }

      // Convert base64 to blob
      const binaryString = atob(base64Data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const audioBlob = new Blob([bytes], { type: "audio/mp3" });
      const audioUrl = URL.createObjectURL(audioBlob);

      // Create new audio element for second chunk
      const audio = new Audio();
      currentAudioRef.current = audio;

      return new Promise<void>((resolve, reject) => {
        const cleanup = () => URL.revokeObjectURL(audioUrl);

        audio.oncanplay = () => {
          audio.play().then(resolve).catch(reject);
        };

        audio.onended = () => {
          setIsSpeaking(false);
          currentAudioRef.current = null;
          cleanup();
          
          // Auto-reengage microphone in conversational mode
          if (conversationalMode && voiceEnabled && speechSupported && !isListening) {
            console.log("Re-engaging microphone after second audio chunk");
            setTimeout(() => {
              startListening();
            }, 750); // Small delay for natural flow
          }
          
          resolve();
        };

        audio.onerror = (e) => {
          console.error("Second chunk audio error:", e);
          setIsSpeaking(false);
          currentAudioRef.current = null;
          cleanup();
          reject(e);
        };

        audio.src = audioUrl;
        audio.load();
      });
    } catch (error) {
      console.error("Second chunk processing failed:", error);
      throw error;
    }
  };

  const stopSpeaking = () => {
    // Stop browser TTS
    if (synthesisRef.current) {
      synthesisRef.current.cancel();
    }

    // Stop OpenAI audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    setIsSpeaking(false);
  };

  // OpenAI TTS function for premium voice quality with faster startup
  const speakWithOpenAI = async (text: string) => {
    console.log("=== OPENAI TTS START ===");
    console.log("Text length:", text.length);
    console.log("Selected voice:", openAIVoice);
    console.log("Use OpenAI Voice setting:", useOpenAIVoice);

    try {
      setIsSpeaking(true);

      // Stop any currently playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = "";
        currentAudioRef.current = null;
      }

      // For long text, process the first chunk immediately for faster startup
      let textToProcess = text;
      let remainingText = '';
      
      // If text is long, split it for faster initial response (reduced threshold for quicker response)
      if (text.length > 300) {
        const sentences = text.match(/[^\.!?]+[\.!?]+/g) || [text];
        // The TTS endpoint hard-caps input at 4000 chars, so take the first
        // third of the sentences but stop early if that would overrun the cap —
        // otherwise a long answer gets sliced mid-word.
        const TTS_CHAR_CAP = 3800;
        const target = Math.max(1, Math.floor(sentences.length / 3));
        let taken = 0;
        let cut = 0;
        while (cut < target && taken + sentences[cut].length <= TTS_CHAR_CAP) {
          taken += sentences[cut].length;
          cut++;
        }
        if (cut > 0 && cut < sentences.length) {
          textToProcess = sentences.slice(0, cut).join('');
          remainingText = sentences.slice(cut).join('');
          console.log("Splitting long text - First chunk:", textToProcess.length, "chars");
        }
      }

      console.log("Making TTS request with voice:", openAIVoice);

      const response = await callAIFunction("tts", textToProcess, {
        voice: openAIVoice,
        model: "tts-1-hd", // Use higher quality model for better sound
      });

      console.log("=== TTS RESPONSE ===");
      console.log("Response success:", response.success);
      console.log("Response has content:", !!response.content);
      console.log("Response error:", response.error);

      if (response.success && response.content) {
          console.log("Processing OpenAI TTS audio response...");

          try {
            const audioData = response.content;
            console.log("Audio data format:", audioData.substring(0, 50));

            // Extract base64 data from data URL
            let base64Data = audioData;
            if (audioData.includes(',')) {
              base64Data = audioData.split(',')[1];
            }

            // Validate base64 format
            if (!/^[A-Za-z0-9+/]*={0,2}$/.test(base64Data)) {
              throw new Error("Invalid base64 audio format");
            }

            // Convert base64 to blob
            const binaryString = atob(base64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            console.log("Creating audio blob, size:", bytes.length);
            const audioBlob = new Blob([bytes], { type: "audio/mp3" });
            const audioUrl = URL.createObjectURL(audioBlob);
            console.log("Created blob URL:", audioUrl);

            // Create audio element and play
            const audio = new Audio();
            currentAudioRef.current = audio;

            // Set up event handlers
            const playPromise = new Promise<void>((resolve, reject) => {
              let isResolved = false;

              const cleanup = () => {
                if (!isResolved) {
                  URL.revokeObjectURL(audioUrl);
                  isResolved = true;
                }
              };

              const timeout = setTimeout(() => {
                if (!isResolved) {
                  console.error("Audio timeout");
                  cleanup();
                  reject(new Error("Audio timeout"));
                }
              }, 15000);

              audio.oncanplay = () => {
                if (!isResolved) {
                  console.log("Audio ready, starting playback");
                  clearTimeout(timeout);
                  audio.play()
                    .then(() => {
                      if (!isResolved) {
                        console.log("Audio playback started successfully");
                        isResolved = true;
                        resolve();
                      }
                    })
                    .catch((err) => {
                      if (!isResolved) {
                        console.error("Audio play failed:", err);
                        cleanup();
                        reject(err);
                      }
                    });
                }
              };

              audio.onended = () => {
                console.log("Audio playback finished");
                setIsSpeaking(false);
                currentAudioRef.current = null;
                cleanup();
                
                // Auto-reengage microphone in conversational mode
                if (conversationalMode && voiceEnabled && speechSupported && !isListening) {
                  console.log("Re-engaging microphone after AI speech");
                  setTimeout(() => {
                    startListening();
                  }, 750); // Slightly longer delay for natural flow
                }
              };

              audio.onerror = (e) => {
                console.error("Audio error:", e, audio.error);
                setIsSpeaking(false);
                currentAudioRef.current = null;
                clearTimeout(timeout);
                if (!isResolved) {
                  cleanup();
                  reject(new Error("Audio playback error"));
                }
              };

              // Load the audio
              audio.src = audioUrl;
              audio.load();
            });

            await playPromise;
            
            // If there's remaining text, process it and queue it up
            if (remainingText.trim()) {
              console.log("Processing remaining text chunk:", remainingText.length, "chars");
              try {
                const secondResponse = await callAIFunction("tts", remainingText, {
                  voice: openAIVoice,
                  model: "tts-1-hd",
                });
                
                if (secondResponse.success && secondResponse.content) {
                  // Queue the second audio to play after the first finishes
                  const currentAudio = currentAudioRef.current;
                  if (currentAudio) {
                    const originalOnEnded = currentAudio.onended;
                    currentAudio.onended = async () => {
                      try {
                        console.log("Playing second audio chunk");
                        await playSecondChunk(secondResponse.content);
                      } catch (e) {
                        console.error("Second chunk playback failed:", e);
                      }
                      if (originalOnEnded) {
                        originalOnEnded.call(currentAudio);
                      }
                    };
                  }
                }
              } catch (secondChunkError) {
                console.log("Second chunk failed, will use browser TTS for remainder");
              }
            }

          } catch (audioError) {
            console.error("OpenAI audio processing failed:", audioError);
            console.log("Falling back to browser TTS");
            setIsSpeaking(false);
            currentAudioRef.current = null;
            speakText(text);
          }
      } else {
        console.error("OpenAI TTS request failed:", response.error);
        console.log("Falling back to browser TTS due to failed request");
        setIsSpeaking(false);
        speakText(text);
      }
    } catch (error) {
      console.error("OpenAI TTS error:", error);
      console.log("Falling back to browser TTS due to exception");
      setIsSpeaking(false);
      speakText(text);
    }
  };

  // Separate function to process messages for better reusability
  const processMessage = async (messageContent: string) => {
    // Snapshot history before adding the placeholder
    const historySnapshot = messages.slice(-10);
    const sermonLibraryContext = sermons.map(sermon => {
      const plainText = (sermon.content || '').replace(/<[^>]*>/g, '').trim();
      const isFocused = sermon.id === focusedSermonId;
      return {
        id: sermon.id,
        title: sermon.title,
        scripture: sermon.scripture,
        date: sermon.date,
        series: sermon.series?.title,
        contentPreview: isFocused
          ? plainText
          : (plainText.substring(0, 500) + (plainText.length > 500 ? '...' : '')),
        isFocused,
      };
    });

    const streamingId = (Date.now() + 1).toString();
    streamingMsgIdRef.current = streamingId;
    let accumulated = '';

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Add empty placeholder — tokens will fill it in real-time
    setMessages(prev => [...prev, {
      id: streamingId,
      role: 'assistant' as const,
      content: '',
      timestamp: new Date(),
    }]);

    try {
      await callAIFunctionStream(
        'theologicalAssistant',
        messageContent,
        {
          conversationHistory: historySnapshot,
          sermonLibrary: sermonLibraryContext.slice(0, 20),
          ministryProfile: Object.keys(ministryProfile).length > 0 ? ministryProfile : undefined,
        },
        (chunk) => {
          accumulated += chunk;
          if (!isStreaming) setIsStreaming(true);
          setMessages(prev => prev.map(msg =>
            msg.id === streamingId ? { ...msg, content: accumulated } : msg
          ));
        },
        abortController.signal,
      );

      // Speak the full response once streaming is complete
      if (voiceEnabled && accumulated) {
        if (isListening) stopListening();
        if (useOpenAIVoice) {
          speakWithOpenAI(accumulated);
        } else {
          speakText(accumulated);
        }
      } else if (conversationalMode && voiceEnabled && speechSupported) {
        setTimeout(() => startListening(), 500);
      }
    } catch (error) {
      const wasAborted = error instanceof Error && error.name === 'AbortError';

      if (wasAborted) {
        // User stopped the response — keep whatever text arrived, or clean up empty placeholder
        if (!accumulated) {
          setMessages(prev => prev.filter(msg => msg.id !== streamingId));
        }
        return;
      }

      // Remove the placeholder on error so we can show the error message instead
      setMessages(prev => prev.filter(msg => msg.id !== streamingId));

      let errorText = "I apologize, but I'm having trouble connecting right now. Please try again in a moment.";
      if (error instanceof Error) {
        if (error.message.includes("timed out") || error.message.includes("timeout")) {
          errorText = "The request took too long to process. Please try asking a shorter question or try again in a moment.";
        } else if (error.message.includes("Connection failed") || error.message.includes("Failed to fetch") || error.message.includes("network")) {
          errorText = "Unable to connect to the AI service. Please check your internet connection and try again.";
        } else if (error.message.includes("Authentication failed")) {
          errorText = "Authentication error. Please refresh the page and sign in again.";
        } else if (error.message.includes("Too many requests")) {
          errorText = "You're making requests too quickly. Please wait a moment and try again.";
        } else if (error.message.includes("Server error")) {
          errorText = "The AI service is experiencing technical difficulties. Please try again in a few minutes.";
        } else if (error.message.includes("Invalid request")) {
          errorText = "There was an issue with your request. Please try rephrasing your question.";
        } else {
          errorText = `I encountered an error: ${error.message}. Please try again or contact support if this continues.`;
        }
      }

      setMessages(prev => [...prev, {
        id: (Date.now() + 2).toString(),
        role: 'assistant' as const,
        content: errorText,
        timestamp: new Date(),
      }]);
    } finally {
      streamingMsgIdRef.current = null;
      abortControllerRef.current = null;
      setIsStreaming(false);
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputText.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: inputText.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageContent = inputText.trim();
    setInputText("");
    setIsLoading(true);

    await processMessage(messageContent);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputText.trim() || isLoading) return;
    sendMessage();
  };

  // Auto-resize textarea for better conversation flow
  const adjustTextareaHeight = (element: HTMLTextAreaElement) => {
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, 120)}px`;
  };

  // ── Shared chat body ────────────────────────────────────────────────────────
  const chatBody = (
    <>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg ${
                message.role === "user"
                  ? "bg-electric text-white"
                  : "bg-sand/30 dark:bg-slate/20 text-midnight dark:text-soft-white"
              }`}
            >
              {message.role === "assistant" ? (
                <div className="text-sm prose prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0 prose-headings:my-2 prose-headings:text-midnight dark:prose-headings:text-soft-white prose-strong:text-midnight dark:prose-strong:text-soft-white">
                  {message.content ? (
                    <>
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                      {message.id === streamingMsgIdRef.current && isStreaming && (
                        <span className="inline-block w-0.5 h-3.5 bg-current opacity-70 animate-pulse ml-0.5 align-middle" />
                      )}
                    </>
                  ) : (
                    message.id === streamingMsgIdRef.current && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )
                  )}
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              )}
              <p className="text-xs opacity-70 mt-1">
                {message.timestamp.toLocaleTimeString()}
              </p>
            </div>
          </div>
        ))}
        {isLoading && !streamingMsgIdRef.current && (
          <div className="flex justify-start">
            <div className="bg-sand/30 dark:bg-slate/20 text-midnight dark:text-soft-white p-3 rounded-lg">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Voice Configuration */}
      {voiceEnabled && (
        <div className="px-4 py-2 bg-sand/20 dark:bg-charcoal/50 border-t border-sand dark:border-slate/30 flex-shrink-0">
          <div className="space-y-2">
            {useOpenAIVoice && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate dark:text-soft-white/60">Voice:</span>
                <select
                  value={openAIVoice}
                  onChange={(e) => setOpenAIVoice(e.target.value as any)}
                  className="text-xs border border-sand dark:border-slate/40 rounded px-2 py-1 min-w-[120px] bg-white dark:bg-charcoal text-midnight dark:text-soft-white"
                >
                  <option value="alloy">Alloy (Neutral)</option>
                  <option value="echo">Echo (Male)</option>
                  <option value="fable">Fable (British)</option>
                  <option value="onyx">Onyx (Deep)</option>
                  <option value="nova">Nova (Female)</option>
                  <option value="shimmer">Shimmer (Soft)</option>
                </select>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate dark:text-soft-white/60">Conversational:</span>
              <button
                onClick={() => setConversationalMode(!conversationalMode)}
                className={`text-xs px-2 py-1 rounded border ${
                  conversationalMode
                    ? "bg-emerald/10 text-emerald border-emerald/30"
                    : "bg-sand/20 dark:bg-slate/20 text-slate dark:text-soft-white/60 border-sand dark:border-slate/40"
                }`}
                title={conversationalMode ? "Auto-send & auto-listen enabled" : "Manual mode"}
              >
                {conversationalMode ? "Auto" : "Manual"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input */}
      <form
        onSubmit={handleSubmit}
        className="p-4 border-t border-sand dark:border-slate/30 flex-shrink-0 mt-auto"
      >
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Book className="h-3.5 w-3.5 text-slate dark:text-soft-white/50 flex-shrink-0" />
            <select
              value={focusedSermonId ?? ''}
              onChange={(e) => setFocusedSermonId(e.target.value || null)}
              className="flex-1 text-xs border border-sand dark:border-slate/40 rounded px-2 py-1 bg-white dark:bg-charcoal text-midnight dark:text-soft-white truncate"
              title={focusedSermonId ? sermons.find(s => s.id === focusedSermonId)?.title : 'All sermons (brief preview)'}
            >
              <option value="">All sermons (brief preview)</option>
              {sermons.map(sermon => (
                <option key={sermon.id} value={sermon.id}>
                  {sermon.title}{sermon.scripture ? ` — ${sermon.scripture}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <textarea
              value={inputText}
              onChange={(e) => {
                setInputText(e.target.value);
                adjustTextareaHeight(e.target);
              }}
              placeholder="Type your request here"
              className="flex-1 px-3 py-2 border border-sand dark:border-slate/40 rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent text-sm resize-none min-h-[40px] max-h-[120px] bg-white dark:bg-charcoal text-midnight dark:text-soft-white"
              disabled={isLoading}
              rows={1}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            {speechSupported && (
              <button
                type="button"
                onClick={isListening ? stopListening : startListening}
                className={`p-2 rounded-lg ${
                  isListening
                    ? "bg-crimson text-white"
                    : "bg-sand/30 dark:bg-slate/20 text-slate dark:text-soft-white/60 hover:bg-sand dark:hover:bg-slate/30"
                }`}
                title={isListening ? "Stop listening" : "Start voice input"}
                disabled={isLoading}
              >
                {isListening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            )}
            {isSpeaking && (
              <button
                type="button"
                onClick={stopSpeaking}
                className="p-2 bg-crimson text-white rounded-lg hover:bg-crimson/90"
                title="Stop speaking"
              >
                <VolumeX className="h-4 w-4" />
              </button>
            )}
            {isLoading ? (
              <button
                type="button"
                onClick={() => abortControllerRef.current?.abort()}
                className="p-2 bg-crimson text-white rounded-lg hover:bg-crimson/90"
                title="Stop generating"
              >
                <Square className="h-4 w-4 fill-current" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={!inputText.trim()}
                className="p-2 bg-electric text-white rounded-lg hover:bg-electric/90 disabled:opacity-50"
                title="Send message (Enter)"
              >
                <Send className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="text-xs text-slate dark:text-soft-white/40">
            {conversationalMode && voiceEnabled
              ? "Conversational mode: Auto-send & auto-listen enabled"
              : "Press Enter to send • Shift+Enter for new line"
            } • Has access to your {sermons.length} sermons for analysis & feedback
          </div>
        </div>
      </form>
    </>
  );

  // ── Shared header controls (voice toggles) ───────────────────────────────
  const voiceControls = speechSupported ? (
    <div className="flex items-center space-x-1">
      <button
        onClick={() => setVoiceEnabled(!voiceEnabled)}
        className={`p-1 rounded ${voiceEnabled ? "text-electric" : "text-white/70"} hover:text-electric`}
        title={voiceEnabled ? "Disable voice" : "Enable voice"}
      >
        {voiceEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
      </button>
      {voiceEnabled && (
        <button
          onClick={() => setUseOpenAIVoice(!useOpenAIVoice)}
          className={`p-1 rounded text-xs ${useOpenAIVoice ? "text-emerald" : "text-white/70"} hover:text-emerald`}
          title={useOpenAIVoice ? "Using OpenAI voice" : "Using browser voice"}
        >
          AI
        </button>
      )}
    </div>
  ) : null;

  // ── Panel mode ────────────────────────────────────────────────────────────
  if (mode === 'panel') {
    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-sand/30 dark:border-slate/30 bg-midnight dark:bg-charcoal text-soft-white flex-shrink-0">
          <div className="flex items-center space-x-2">
            <Book className="h-4 w-4" />
            <span className="text-sm font-medium">Theological Assistant</span>
          </div>
          {voiceControls}
        </div>
        {chatBody}
      </div>
    );
  }

  // ── Float mode: FAB ───────────────────────────────────────────────────────
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed w-16 h-16 bg-electric text-white rounded-full shadow-2xl hover:bg-electric/90 transition-all duration-200 flex items-center justify-center border-2 border-white/20 ${
          isMobile ? 'bottom-20 right-4' : 'bottom-6 right-6'
        }`}
        style={{
          zIndex: 99999,
          position: "fixed",
          bottom: isMobile ? "80px" : "24px",
          right: isMobile ? "16px" : "24px",
        }}
        title="Open Theological Assistant"
      >
        <Book className="h-7 w-7" />
      </button>
    );
  }

  // ── Float mode: expanded modal ────────────────────────────────────────────
  if (isExpanded) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center bg-black/50"
        style={{ zIndex: 1000 }}
      >
        <div className="w-[80vw] max-w-4xl h-[75vh] bg-white dark:bg-charcoal rounded-xl shadow-2xl border border-sand dark:border-slate/40 flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-sand/30 dark:border-slate/30 bg-midnight dark:bg-charcoal text-soft-white rounded-t-xl flex-shrink-0">
            <div className="flex items-center space-x-2">
              <Book className="h-5 w-5" />
              <h3 className="font-medium">Theological Assistant</h3>
            </div>
            <div className="flex items-center space-x-1">
              {voiceControls}
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 text-white/70 hover:text-white"
                title="Collapse to window"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-white/70 hover:text-white"
                title="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          {chatBody}
        </div>
      </div>
    );
  }

  // ── Float mode: normal window ─────────────────────────────────────────────
  return (
    <div
      className={`fixed w-96 max-w-[calc(100vw-2rem)] bg-white dark:bg-charcoal rounded-lg shadow-2xl border border-sand dark:border-slate/40 ${
        isMinimized ? "h-16" : "h-[600px] max-h-[calc(100vh-2rem)]"
      } transition-all duration-200 flex flex-col`}
      style={{
        zIndex: 99999,
        position: "fixed",
        bottom: isMobile ? "80px" : "16px",
        right: "16px",
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-sand/30 dark:border-slate/30 bg-midnight dark:bg-charcoal text-soft-white rounded-t-lg flex-shrink-0">
        <div className="flex items-center space-x-2">
          <Book className="h-5 w-5" />
          <h3 className="font-medium">Theological Assistant</h3>
        </div>
        <div className="flex items-center space-x-1">
          {voiceControls}
          <button
            onClick={() => setIsExpanded(true)}
            className="p-1 text-white/70 hover:text-white"
            title="Expand to full screen"
          >
            <Maximize2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1 text-white/70 hover:text-white"
            title={isMinimized ? "Restore" : "Minimize"}
          >
            <Minimize2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 text-white/70 hover:text-white"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {!isMinimized && chatBody}
    </div>
  );
}